// Final pay, in the order the law and the database require: the leave balance
// read from SimplePay, a schedule prepared by the manager from the source
// records, approved by finance (never by the preparer), then relayed to
// SimplePay. Every line shows where its figure came from. Nothing is computed
// here; driver_final_pay_compute_v1 does it and this panel lays it out.
//
// A driver already paid on a finalised SimplePay payslip is recorded from that
// payslip instead (9800), because approving a schedule always relays it and
// would pay them twice.
import { useState } from 'react'
import { CalendarClock, ReceiptText, Send, Wallet } from 'lucide-react'
import {
  useApproveFinalPay, useGetFinalPayPreview, usePrepareFinalPay, useReadOffboardingLeave,
  useRecordFinalPayPaidOnSimplePay, useRelayFinalPay,
} from '../../hooks/backend/offboarding'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../lib/shadcn/alert-dialog'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import {
  formatDay, formatWhen, formatZar, type CaseDetail, type Outstanding, type PayLine, type Schedule,
} from '../../utils/offboarding'

export function FinalPayPanel({ detail, onChanged }: { detail: CaseDetail; onChanged: () => void }) {
  const role = detail.final_pay_role
  const fp = detail.final_pay
  const c = detail.case
  const caseId = detail.summary.id
  const editable = ['open', 'awaiting_signoff'].includes(detail.summary.status)
  const paid = detail.items.find((i) => i.item_no === 16)?.status === 'done'

  const readLeave = useReadOffboardingLeave<{ ok: boolean; leave_days: number }>()
  const preview = useGetFinalPayPreview<{ ok: boolean; schedule: Schedule }>()
  const prepare = usePrepareFinalPay<{ ok: boolean; net: number }>()
  const approve = useApproveFinalPay<{ ok: boolean }>()
  const relay = useRelayFinalPay<{ ok: boolean; enter_by_hand: unknown }>()
  const paidOn = useRecordFinalPayPaidOnSimplePay<{ ok: boolean; net: number }>()
  const [confirmRelay, setConfirmRelay] = useState(false)
  const [paidOnOpen, setPaidOnOpen] = useState(false)
  const [payslipId, setPayslipId] = useState('')

  if (!role.can_see || !fp) return null

  async function run(what: string, fn: () => Promise<unknown>) {
    try { await fn(); toast.success(what); onChanged() } catch (err) { toast.error(writeErrorMessage(err)) }
  }

  const schedule: Schedule | null = preview.data?.schedule ?? null
  const slip = fp.prepared ? (fp.basis?.paid_on_simplepay as PaidOnSimplePay | undefined) ?? null : null

  return (
    <section className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Wallet className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Final pay</h3>
        <Chip tone={detail.summary.days_to_pay_due < 0 && !paid ? 'red' : 'neutral'} icon={<CalendarClock className="h-3 w-3" />}>
          Paid on payday, {formatDay(detail.summary.pay_due_by)}
        </Chip>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">Annual leave from SimplePay:</span>
        {c.leave_days_simplepay === null
          ? <span className="font-medium text-amber-700 dark:text-amber-300">not read yet</span>
          : <span className="font-medium text-foreground">{Number(c.leave_days_simplepay).toFixed(4)} days on {formatDay(c.leave_read_for_date)} (read {formatWhen(c.leave_read_at)})</span>}
        {editable && (role.can_prepare || role.can_check) && (
          <Button size="sm" variant="outline" disabled={readLeave.loading || !c.dates_confirmed}
            onClick={() => void run('Leave balance read from SimplePay', () => readLeave.trigger({ case_id: caseId }).result)}>
            {readLeave.loading && <Spinner className="mr-1 h-3 w-3" />} {c.leave_days_simplepay === null ? 'Read from SimplePay' : 'Read again'}
          </Button>
        )}
        {!c.simplepay_linked && <span className="text-rose-700 dark:text-rose-300">This employee is not linked to SimplePay.</span>}
      </div>

      {editable && !paid && (role.can_prepare || role.can_check) && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={preview.loading}
            onClick={() => void preview.trigger({ case_id: caseId }, { skipCache: true }).catch((e) => toast.error(writeErrorMessage(e)))}>
            {preview.loading && <Spinner className="mr-1 h-3 w-3" />} Preview the schedule
          </Button>
          {role.can_prepare && (
            <Button size="sm" variant="brand" disabled={prepare.loading || c.leave_days_simplepay === null}
              onClick={() => void run('Final pay prepared', () => prepare.trigger({ case_id: caseId }).result)}>
              {prepare.loading && <Spinner className="mr-1 h-3 w-3" />} {fp.prepared ? 'Prepare again' : 'Prepare final pay'}
            </Button>
          )}
          {role.can_check && !(fp.prepared && fp.relay?.relayed_at) && (
            <Button size="sm" variant="outline" onClick={() => setPaidOnOpen(true)}>
              <ReceiptText className="mr-1 h-3 w-3" /> Already paid on SimplePay
            </Button>
          )}
        </div>
      )}

      {schedule && (
        <div className="rounded-md border border-dashed border-border p-2">
          <p className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Preview, not saved</p>
          <ScheduleTable lines={schedule.lines} outstanding={schedule.outstanding}
            earnings={schedule.earnings} deductions={schedule.deductions} net={schedule.net} />
        </div>
      )}

      {fp.prepared && slip ? (
        <div className="space-y-2 text-xs">
          <p className="text-foreground">
            Paid on SimplePay payslip <span className="font-medium">{slip.payslip_id}</span> dated {formatDay(slip.payslip_date)},
            nett <span className="font-medium tabular-nums">{formatZar(slip.nett)}</span>, in payment run {slip.payment_run_id}.
          </p>
          <p className="text-muted-foreground">
            Read from SimplePay and recorded by {fp.prepared_by} on {formatWhen(fp.prepared_at)}. Nothing was sent to SimplePay from the case.
          </p>
        </div>
      ) : fp.prepared ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Prepared by {fp.prepared_by} on {formatWhen(fp.prepared_at)} for the period {formatDay(fp.period_start)} to {formatDay(fp.period_end)},
            on the SimplePay payslip dated {formatDay(fp.payslip_date)}.
            {fp.approved_by ? ` Approved by ${fp.approved_by} on ${formatWhen(fp.approved_at)}.` : ' Not approved yet.'}
          </p>
          <ScheduleTable lines={fp.lines} outstanding={fp.outstanding} earnings={fp.earnings} deductions={fp.deductions} net={fp.net} />

          {editable && !fp.approved_by && role.can_check && (
            <Button size="sm" variant="brand" disabled={approve.loading}
              onClick={() => void run('Final pay approved', () => approve.trigger({ case_id: caseId }).result)}>
              {approve.loading && <Spinner className="mr-1 h-3 w-3" />} Approve and send to SimplePay
            </Button>
          )}

          {fp.approved_by && fp.relay && (
            <div className="space-y-2 rounded-md bg-muted/40 p-2 text-xs">
              <p className="font-medium text-foreground">Relay to SimplePay (employee {fp.relay.simplepay_employee_id ?? 'not linked'}, payslip {formatDay(fp.relay.payslip_date)})</p>
              <ul className="grid gap-0.5 sm:grid-cols-2">
                {Object.entries(fp.relay.attributes).map(([k, v]) => (
                  <li key={k} className="flex justify-between gap-2"><code className="text-[11px]">{k}</code><span className="tabular-nums">{v}</span></li>
                ))}
              </ul>
              {fp.relay.enter_by_hand.length > 0 && (
                <div>
                  <p className="font-medium text-foreground">Entered by hand on SimplePay, because its API does not take them:</p>
                  <ul className="list-disc pl-5">
                    {fp.relay.enter_by_hand.map((m) => <li key={m.label}>{m.label}: {formatZar(m.amount)}. {m.where}.</li>)}
                  </ul>
                </div>
              )}
              {fp.relay.relayed_at
                ? <p className="text-foreground">Relayed {formatWhen(fp.relay.relayed_at)}, SimplePay answered HTTP {fp.relay.relay_response?.http_status ?? '?'}. The case notes carry its answer.</p>
                : (role.can_check || role.can_prepare) && editable && (
                  <Button size="sm" variant="outline" onClick={() => setConfirmRelay(true)} disabled={relay.loading}>
                    <Send className="mr-1 h-3 w-3" /> Send to SimplePay again
                  </Button>
                )}
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Not prepared yet. {detail.items.find((i) => i.item_no === 13)?.owner ?? 'The manager'} prepares it once the dates are confirmed and the leave balance is read.
        </p>
      )}

      <AlertDialog open={paidOnOpen} onOpenChange={setPaidOnOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Final pay already paid on SimplePay?</AlertDialogTitle>
            <AlertDialogDescription>
              Use this when the final pay went out on a normal payslip that is finalised and paid. The case reads the payslip back
              from SimplePay and fills steps 13 to 16 from it, without sending anything. A payslip dated before the one the last
              working day ({formatDay(c.last_working_day)}) falls on is refused, because the days after it are still unpaid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">SimplePay payslip number</span>
            <Input inputMode="numeric" value={payslipId} placeholder="79383691"
              onChange={(e) => setPayslipId(e.target.value.replace(/\D/g, ''))} />
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={!payslipId || paidOn.loading}
              onClick={() => void run('Final pay recorded as paid on SimplePay',
                () => paidOn.trigger({ case_id: caseId, payslip_id: payslipId }).result)}>
              Record it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRelay} onOpenChange={setConfirmRelay}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send final pay to SimplePay?</AlertDialogTitle>
            <AlertDialogDescription>
              This writes the inputs above onto the employee's SimplePay payslip. It is not retried automatically: if SimplePay does not answer, check the payslip on SimplePay before sending again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void run('Sent to SimplePay', () => relay.trigger({ case_id: caseId }).result)}>Send</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

type PaidOnSimplePay = { payslip_id: string; payslip_date: string; nett: number; payment_run_id: string }

function ScheduleTable({ lines, outstanding, earnings, deductions, net }: {
  lines: PayLine[]; outstanding: Outstanding[]; earnings: number; deductions: number; net: number
}) {
  return (
    <div className="space-y-2">
      <table className="w-full text-xs">
        <tbody className="divide-y divide-border">
          {lines.map((l) => (
            <tr key={l.key} className="align-top">
              <td className="py-1.5 pr-2">
                <p className="font-medium text-foreground">{l.label}</p>
                <p className="text-muted-foreground">{l.basis}</p>
                <p className="text-[10px] text-muted-foreground/80">Source: {l.source}</p>
              </td>
              <td className={`whitespace-nowrap py-1.5 text-right tabular-nums ${l.kind === 'deduction' ? 'text-rose-700 dark:text-rose-300' : 'text-foreground'}`}>
                {l.amount === null ? '—' : `${l.kind === 'deduction' ? '- ' : ''}${formatZar(l.amount)}`}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-border text-foreground">
          <tr><td className="pt-1.5">Earnings</td><td className="pt-1.5 text-right tabular-nums">{formatZar(earnings)}</td></tr>
          <tr><td>Deductions</td><td className="text-right tabular-nums">- {formatZar(deductions)}</td></tr>
          <tr className="font-semibold"><td>Net final pay</td><td className="text-right tabular-nums">{formatZar(net)}</td></tr>
        </tfoot>
      </table>
      {outstanding.length > 0 && (
        <div className="rounded-md border border-amber-300/60 bg-amber-50 p-2 text-xs dark:border-amber-700/50 dark:bg-amber-950/40">
          <p className="font-medium text-foreground">Outstanding, not taken from final pay, for a separate decision</p>
          <ul className="mt-1 space-y-1">
            {outstanding.map((o, i) => (
              <li key={i}><span className="font-medium">{o.label}{o.amount !== null ? `: ${formatZar(o.amount)}` : ''}.</span> {o.detail}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
